import { Badge, Button, Paper, Progress, Stack, Text } from '@mantine/core';
import { IconCheck, IconDeviceHeartMonitor, IconMail, IconRefresh, IconRouter, IconX } from '@tabler/icons-react';
import { GetTuyaDeviceResponse } from 'api/actions/get-tuya-device/get-tuya-device-response';
import { GetTuyaPairingSessionResponse } from 'api/actions/get-tuya-pairing-session/get-tuya-pairing-session-response';
import { useApi } from 'api/api-context';
import useLang from 'lang';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import theme from 'theme';
import H2SemiBold from 'theme/components/Typography/H2SemiBold';
import P1Regular from 'theme/components/Typography/P1Regular';
import P2Regular from 'theme/components/Typography/P2Regular';

type PairingSession = GetTuyaPairingSessionResponse;
type Step = 'instructions' | 'discovering' | 'success' | 'failure';

const MAX_DISCOVERY_SECONDS = 60;
const SUPPORT_EMAIL = 'info@phillip.city';

/**
 * End-user Zigbee pairing workflow for one accessible Tuya gateway.
 */
export default function ZigbeePairing({ gateway }: { gateway: GetTuyaDeviceResponse }) {
  const { _t } = useLang();
  const { getAction } = useApi();
  const [step, setStep] = useState<Step>('instructions');
  const [session, setSession] = useState<PairingSession | null>(null);
  const [pairedDeviceName, setPairedDeviceName] = useState<string | null>(null);
  const [timeLeft, setTimeLeft] = useState(MAX_DISCOVERY_SECONDS);
  const pollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const countdownIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const clientDeadlineRef = useRef<number | null>(null);
  const mountedRef = useRef(true);

  const clearTimers = useCallback(() => {
    if (pollTimeoutRef.current) {
      clearTimeout(pollTimeoutRef.current);
      pollTimeoutRef.current = null;
    }
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
  }, []);

  const updateCountdown = useCallback((expiresAt: string) => {
    const seconds = Math.max(0, Math.ceil((new Date(expiresAt).getTime() - Date.now()) / 1000));
    setTimeLeft(Math.min(MAX_DISCOVERY_SECONDS, seconds));
  }, []);

  const getEffectiveExpiresAt = useCallback((expiresAt: string) => {
    const serverExpiry = new Date(expiresAt).getTime();
    return new Date(Math.min(serverExpiry, clientDeadlineRef.current ?? serverExpiry)).toISOString();
  }, []);

  const finishWithFailure = useCallback(() => {
    clearTimers();
    setStep('failure');
  }, [clearTimers]);

  const handleSessionUpdate = useCallback(
    (nextSession: PairingSession) => {
      if (!mountedRef.current) return;

      setSession(nextSession);

      if (nextSession.status === 'succeeded') {
        clearTimers();
        setPairedDeviceName(nextSession.device?.label || _t('New Zigbee sensor'));
        setStep('success');
        return;
      }

      if (nextSession.status === 'timed_out' || nextSession.status === 'failed') {
        finishWithFailure();
        return;
      }

      const effectiveExpiresAt = getEffectiveExpiresAt(nextSession.expiresAt);
      updateCountdown(effectiveExpiresAt);
      if (!countdownIntervalRef.current) {
        countdownIntervalRef.current = setInterval(() => updateCountdown(effectiveExpiresAt), 1000);
      }
      setStep('discovering');
    },
    [_t, clearTimers, finishWithFailure, getEffectiveExpiresAt, updateCountdown]
  );

  const pollSession = useCallback(
    (sessionId: string) => {
      const getPairingSession = getAction('GetTuyaPairingSession');

      getPairingSession({ parameters: { sessionId } })
        .then((nextSession) => {
          handleSessionUpdate(nextSession);

          if (!mountedRef.current || nextSession.status !== 'discovering') return;

          const millisecondsUntilExpiry = new Date(getEffectiveExpiresAt(nextSession.expiresAt)).getTime() - Date.now();
          if (millisecondsUntilExpiry <= 0) {
            finishWithFailure();
            return;
          }

          const nextPollDelay = Math.min(Math.max(nextSession.pollAfterSeconds, 5) * 1000, millisecondsUntilExpiry);
          pollTimeoutRef.current = setTimeout(() => pollSession(sessionId), nextPollDelay);
        })
        .catch(() => finishWithFailure());
    },
    [finishWithFailure, getAction, getEffectiveExpiresAt, handleSessionUpdate]
  );

  const startCountdown = useCallback(
    (expiresAt: string) => {
      if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
      updateCountdown(expiresAt);
      countdownIntervalRef.current = setInterval(() => updateCountdown(expiresAt), 1000);
    },
    [updateCountdown]
  );

  const startPairing = useCallback(() => {
    clearTimers();
    clientDeadlineRef.current = Date.now() + MAX_DISCOVERY_SECONDS * 1000;
    setSession(null);
    setPairedDeviceName(null);
    setStep('discovering');
    setTimeLeft(MAX_DISCOVERY_SECONDS);

    const createPairingSession = getAction('CreateTuyaPairingSession');
    createPairingSession({ parameters: { gatewayTuyaDeviceId: gateway.id } })
      .then((createdSession) => {
        handleSessionUpdate(createdSession);
        startCountdown(getEffectiveExpiresAt(createdSession.expiresAt));
        pollTimeoutRef.current = setTimeout(
          () => pollSession(createdSession.sessionId),
          Math.max(createdSession.pollAfterSeconds, 5) * 1000
        );
      })
      .catch((error: any) => {
        const activeSessionId = error?.response?.data?.activeSession?.sessionId;

        if (error?.response?.status === 409 && activeSessionId) {
          pollSession(activeSessionId);
          return;
        }

        finishWithFailure();
      });
  }, [
    clearTimers,
    finishWithFailure,
    gateway.id,
    getAction,
    getEffectiveExpiresAt,
    handleSessionUpdate,
    pollSession,
    startCountdown,
  ]);

  const reset = useCallback(() => {
    clearTimers();
    clientDeadlineRef.current = null;
    setSession(null);
    setPairedDeviceName(null);
    setTimeLeft(MAX_DISCOVERY_SECONDS);
    setStep('instructions');
  }, [clearTimers]);

  useEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;
      clearTimers();
    };
  }, [clearTimers]);

  const progress = useMemo(() => ((MAX_DISCOVERY_SECONDS - timeLeft) / MAX_DISCOVERY_SECONDS) * 100, [timeLeft]);
  const supportHref = useMemo(() => {
    const subject = encodeURIComponent(_t('Zigbee sensor pairing failed'));
    const body = encodeURIComponent(
      `${_t('Hello, I need help pairing a Zigbee sensor.')}\n\n${_t('Gateway')}: ${gateway.label ?? ''}\n${_t(
        'Session'
      )}: ${session?.sessionId ?? ''}`
    );

    return `mailto:${SUPPORT_EMAIL}?subject=${subject}&body=${body}`;
  }, [_t, gateway.label, session]);

  if (step === 'instructions') {
    return (
      <Paper p="lg" bg="white" style={{ borderRadius: 14 }}>
        <Stack gap="lg">
          <Stack align="center" gap="md">
            <IconDeviceHeartMonitor size={56} color="#551FFF" />
            <H2SemiBold ta="center" color={theme.colors?.gray?.[12]}>
              {_t('Prepare the new Zigbee sensor')}
            </H2SemiBold>
            <P1Regular ta="center" color={theme.colors?.gray?.[10]}>
              {_t(
                'Keep the sensor near the gateway. Press and hold the sensor reset button until its LED starts blinking.'
              )}
            </P1Regular>
          </Stack>

          <Stack gap="xs">
            <Badge color="violet" variant="light" radius="xl">
              {gateway.label || _t('Zigbee gateway')}
            </Badge>
            <P2Regular color={theme.colors?.gray?.[10]}>
              {_t('After you tap Start pairing, the gateway will search for a new sensor for up to 60 seconds.')}
            </P2Regular>
          </Stack>

          <Button size="lg" radius="xl" onClick={startPairing} fullWidth>
            {_t('Start pairing')}
          </Button>
        </Stack>
      </Paper>
    );
  }

  if (step === 'discovering') {
    return (
      <Paper p="lg" bg="white" style={{ borderRadius: 14 }}>
        <Stack align="center" gap="lg">
          <IconRouter size={56} color="#551FFF" />
          <H2SemiBold ta="center" color={theme.colors?.gray?.[12]}>
            {_t('Searching for sensor...')}
          </H2SemiBold>
          <P1Regular ta="center" color={theme.colors?.gray?.[10]}>
            {_t('Put the sensor in pairing mode and keep it close to the gateway until pairing completes.')}
          </P1Regular>
          <Text size="2rem" fw={700} c={theme.colors?.gray?.[12]}>
            {timeLeft}s
          </Text>
          <Progress value={progress} size="xl" radius="xl" color="violet" w="100%" />
          <Button size="lg" radius="xl" variant="secondary" leftSection={<IconX size={18} />} onClick={reset} fullWidth>
            {_t('Cancel')}
          </Button>
        </Stack>
      </Paper>
    );
  }

  if (step === 'success') {
    return (
      <Paper p="lg" bg="white" style={{ borderRadius: 14 }}>
        <Stack align="center" gap="lg">
          <IconCheck size={64} color="#22C55E" />
          <H2SemiBold ta="center" color={theme.colors?.gray?.[12]}>
            {_t('Sensor added')}
          </H2SemiBold>
          <P1Regular ta="center" color={theme.colors?.gray?.[10]}>
            {_t('The new Zigbee sensor was paired successfully.')}
          </P1Regular>
          {pairedDeviceName && (
            <Badge color="green" variant="light" radius="xl">
              {pairedDeviceName}
            </Badge>
          )}
          <Button size="lg" radius="xl" onClick={reset} fullWidth>
            {_t('Done')}
          </Button>
        </Stack>
      </Paper>
    );
  }

  return (
    <Paper p="lg" bg="white" style={{ borderRadius: 14 }}>
      <Stack align="center" gap="lg">
        <IconX size={64} color="#EF4444" />
        <H2SemiBold ta="center" color={theme.colors?.gray?.[12]}>
          {_t('Pairing failed')}
        </H2SemiBold>
        <P1Regular ta="center" color={theme.colors?.gray?.[10]}>
          {_t('The sensor could not be paired. Check that it is reset, close to the gateway, and try again.')}
        </P1Regular>
        <Button size="lg" radius="xl" leftSection={<IconRefresh size={18} />} onClick={reset} fullWidth>
          {_t('Try again')}
        </Button>
        <Button
          component="a"
          href={supportHref}
          size="lg"
          radius="xl"
          variant="secondary"
          leftSection={<IconMail size={18} />}
          fullWidth
        >
          {_t('Contact support')}
        </Button>
      </Stack>
    </Paper>
  );
}
